'use client';

import Link from "next/link";
import { MapPin, ThumbsUp } from "react-feather";
import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import ChevronDown from "../../public/icons/chevron-down.svg";
import { loadNavigationClientSide } from "../../helper/api";
import { NavigationEntry, PersonalNavigationEntry } from "../../helper/interfaces";
import useHeaderHeight from "../../lib/useHeaderHeight";
import { AppContext } from "../../provider/AppContextProvider";
import Loading from "../ui/Loading";
import Toggle from "./Toggle";
import { AppTheme } from "../../helper/enums";
import MyFavs from "./MyFavs";
import NavigationSearch from "../NavigationSearch";
import { getLinkObjectFromTeaser } from "../../helper/link";
import { useModalFocus } from "../../lib/useModalFocus";

type HeaderNavItemProps = {
  href: string;
  children?: ReactNode;
  key: string;
  className?: string;
  active?: boolean;
  data?: string;
  external?: boolean;
};

export type NavigationProps = {
  isCollapsed: boolean;
  showCmp?: Function;
  isApp?: boolean;
  toggleOverlay?: Function;
  triggerRef?: React.RefObject<HTMLElement>;
};

type NavigationItemProps = {
  navigation: NavigationEntry;
  level: number;
  renderSubMenu: Function;
  loadSubEntry: Function;
  openSubEntry: Function;
  closeSubEntry: Function;
  selectEntry: Function;
  toggleNavbar: Function;
  pageId: string | null;
  isApp: boolean;
  pageChanged: boolean;
  editMode: boolean;
  myNews: PersonalNavigationEntry[];
  setMyNews: any;
  addToMyNews: any;
  removeFromMyNews: any;
};

const hasChildId = (childId: string, entries: NavigationEntry[]) => {
  let found = false;
  if (entries) {
    entries.forEach((child) => {
      if (!found && child?.id) {
        if (child.id === childId) found = true;
        else if (child.children) found = hasChildId(childId, child.children);
        else found = false;
      }
    });
  }
  return found;
};

const CmpLink = () => {
  // eslint-disable-next-line
  return (<a href="/cmp" className="nav-item-text">Cookies & Tracking</a>);
};

function NavigationItem({
  navigation,
  level,
  renderSubMenu,
  loadSubEntry,
  openSubEntry,
  closeSubEntry,
  selectEntry,
  toggleNavbar,
  pageId,
  isApp,
  pageChanged,
  editMode,
  myNews,
  setMyNews,
  addToMyNews,
  removeFromMyNews,
}: NavigationItemProps) {
  const hasChildren = navigation.children && navigation.children.length > 0;
  const isOpen =
    (navigation.isOpen == true && hasChildren) ||
    (hasChildId(pageId ?? '', navigation.children) && pageChanged);

  let externalLink = "";
  const linkObject = getLinkObjectFromTeaser(navigation);
  const internalLink = linkObject.internalLink;
  if (!internalLink || internalLink == "") {
    externalLink = linkObject.externalLink ?? '';
  }
  const myNewsIds = myNews ? myNews.map((entry) => entry.id) : [];
  return (
    <React.Fragment>
      <div
        className={`nav-item ${
          navigation.id == pageId ? "nav-item-active" : ""
        } ${isOpen == true ? "nav-item-open" : ""}`}
      >
        {editMode ? (
          <div className="nav-item-text-wrapper">
            <div className="nav-item-text">{navigation.title}</div>
          </div>
        ) : (
          <div className={`nav-item-text-wrapper`}>
            <span className={`pr-2 icon-big`}></span>
            {externalLink && (
              <a
                href={externalLink}
                onClick={() => {
                  if (isApp == false) {
                    toggleNavbar();
                  }
                  selectEntry(navigation.id);
                }}
                target="_blank"
                rel="noreferrer"
                className="nav-item-text"
              >
                {navigation.title}
              </a>
            )}
            {isApp == true && internalLink && (
              <a
                href={internalLink}
                onClick={() => selectEntry(navigation.id)}
                className="nav-item-text"
              >
                {navigation.title}
              </a>
            )}
            {isApp == false && internalLink && (
              <Link
                href={internalLink}
                onClick={() => {
                  toggleNavbar();
                  selectEntry(navigation.id);
                }}
                className="nav-item-text"
              >
                {navigation.title}
              </Link>
            )}
            {!internalLink && !externalLink && (
              <span
                className="nav-item-text"
                onClick={() => {
                  if (navigation.extendable) {
                    loadSubEntry(navigation.id, level);
                  } else if (isOpen) {
                    closeSubEntry(navigation.id, level);
                  } else if (!navigation.loading && hasChildren) {
                    openSubEntry(navigation.id, level);
                  }
                }}
              >
                {navigation.title} {internalLink}
              </span>
            )}
          </div>
        )}
        {(navigation.extendable || hasChildren || navigation.loading) && (
          <div className="nav-item-right">
            {navigation.loading ? (
              <Loading inline small aria-label="Lädt..." />
            ) : (
              <button
                className="nav-item__right__button"
                onClick={() => {
                  if (navigation.extendable) {
                    loadSubEntry(navigation.id, level);
                  } else if (isOpen) {
                    closeSubEntry(navigation.id, level);
                  } else if (!navigation.loading && hasChildren) {
                    openSubEntry(navigation.id, level);
                  }
                }}
              >
                <ChevronDown className="nav-item-chevron" aria-label={isOpen ? "Untermenü schließen" : "Untermenü öffnen"} />
              </button>
            )}
          </div>
        )}
        {editMode && !navigation.loading && (
          <div className="nav-item-right">
            {myNewsIds.includes(navigation.id) ? (
              <div
                className="nav-item-my-news-button nav-item-my-news-button-remove"
                onClick={() => removeFromMyNews(navigation)}
                aria-label="Favorit entfernen"
              >
                <span></span>
              </div>
            ) : (
              <button
                className="nav-item-my-news-button nav-item-my-news-button-add"
                onClick={() => addToMyNews(navigation)}
                disabled={myNews.length >= 3}
                aria-label="Favorit hinzufügen"
              >
                <span>+</span>
              </button>
            )}
          </div>
        )}
      </div>
      {hasChildren && isOpen && (
        <div className="nav-sub-menu">
          {renderSubMenu(
            navigation.children,
            level + 1,
            pageId,
            isApp,
            pageChanged
          )}
        </div>
      )}
    </React.Fragment>
  );
}

const setLoading = (
  id: string,
  entries: NavigationEntry[],
  isLoading: boolean,
  currentLevel: number,
  level: number
) => {
  let found = false;
  if (entries) {
    entries.forEach((child) => {
      if (!found) {
        if (child?.id === id && currentLevel == level) {
          found = true;
          child.loading = isLoading;
          child.extendable = !isLoading;
        } else if (child?.children) {
          child.children = setLoading(id, child.children, isLoading, currentLevel + 1, level);
        } else found = false;
      }
    });
  }
  return entries;
};

const setOpenState = (
  id: string,
  entries: NavigationEntry[],
  isOpen: boolean,
  currentLevel: number,
  level: number
) => {
  let found = false;
  if (entries) {
    entries.forEach((child) => {
      if (!found) {
        if (child.id === id && currentLevel == level) {
          found = true;
          child.isOpen = isOpen;
        } else if (child.children) {
          child.children = setOpenState(id, child.children, isOpen, currentLevel + 1, level);
        } else found = false;
      }
    });
  }
  return entries;
};

const selectSingleEntry = (id: string, entries: NavigationEntry[]) => {
  if (entries) {
    entries.forEach((child) => {
      if (child) {
        if (child.id === id) {
          child.isOpen = true;
        } else {
          child.isOpen = false;
        }

        if (child.children) {
          child.children = selectSingleEntry(id, child.children);
        }
      }
    });
  }
  return entries;
};

const setLoadedItems = (
  id: string,
  entries: NavigationEntry[],
  loadedEntries: NavigationEntry[],
  currentLevel: number,
  level: number
) => {
  let found = false;
  if (entries) {
    entries.forEach((child) => {
      if (!found) {
        if (child?.id === id && currentLevel == level) {
          found = true;
          child.children = loadedEntries;
          child.extendable = false;
          child.isOpen = true;
          child.loading = false;
        } else if (child?.children) {
          child.children = setLoadedItems(id, child.children, loadedEntries, currentLevel + 1, level);
        } else found = false;
      }
    });
  }
  return entries;
};

interface subscriptions {
  id: string;
  text: string;
  isSubscribed: boolean;
}

export default function NavigationComponent(props: NavigationProps) {
  const { pageId, personalResorts, setPersonalResorts, pushSubscriptionId, appInfo } =
    React.useContext(AppContext);
  const [pageChanged, setPageChanged] = useState(false);
  const collapsed = props.isCollapsed;
  const navRef = useRef<HTMLDivElement>(null);

  useModalFocus({
    isOpen: !collapsed,
    onClose: () => {
      toggleOverlay();
    },
    modalRef: navRef as React.RefObject<HTMLElement>,
    triggerRef: props.triggerRef,
  });

  //pushes
  const [subscriptions, setSubscriptions] = useState<subscriptions[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean | null>(null);

  const togglePushes = useCallback(
    (id) => {
      let updatedSubscriptions: subscriptions[] = [];
      subscriptions.forEach((entry: subscriptions) => {
        updatedSubscriptions.push({
          id: entry.id as string,
          text: entry.text as string,
          isSubscribed: (id == entry.id
            ? !entry.isSubscribed
            : entry.isSubscribed) as boolean,
        });
      });
      if (!isProcessing) {
        if (window.pushesSetSubscriptions) {
          setIsProcessing(true);
          window.pushesSetSubscriptions(updatedSubscriptions as any);
          setSubscriptions(updatedSubscriptions);
        }
      }
    },
    [subscriptions, isProcessing]
  );

  useEffect(() => {
    window.pushesSubscriptionsCallback = (
      data: [{ id: string; text: string; isSubscribed: boolean }]
    ) => {
      setSubscriptions(data);
      setIsProcessing(false);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (window.pushesIsSubscribed != undefined) {
        window.pushesIsSubscribed();
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  //pushes

  useEffect(() => {
    setPageChanged(true);
  }, [pageId]);

  const toggleOverlay = () => {
    if (props.toggleOverlay) {
      props.toggleOverlay();
    }
  };

  const showCmp = () => {
    if (props.showCmp) {
      props.showCmp();
    }
  };

  const initNavigation = async () => {
    let result: NavigationEntry | null = await loadNavigationClientSide(undefined);
    if (result) {
      setNavigation(result);
    }
  };

  useEffect(() => {
    initNavigation();
  }, []);

  const [navigation, setNavigation] = useState<NavigationEntry | null>(null);

  const setEntryLoading = useCallback(
    async (id: string, isLoading: boolean, level: number) => {
      setNavigation((prevState: NavigationEntry) => {
        return {
          ...prevState,
            children: setLoading(id, prevState.children, isLoading, 0, level),
            closedId: null,
        };
      });
    },
    [setNavigation]
  );

  const loadSubEntry = useCallback(
    async (id: string, level: number) => {
      setEntryLoading(id, true, level);
      const loadedEntries = await loadNavigationClientSide(id);
      if (loadedEntries) {
        setNavigation((prevState: NavigationEntry) => {
          return {
            ...prevState,
            children: setLoadedItems(
              id,
              prevState.children,
              loadedEntries.children,
              0,
              level
            ),
          };
        });
      } else {
        setEntryLoading(id, false, level);
      }
    },
    [setNavigation, setEntryLoading]
  );

  const openSubEntry = useCallback(
    async (id: string, level: number) => {
      setNavigation((prevState: NavigationEntry) => {
        return {
          ...prevState,
          children: setOpenState(id, prevState.children, true, 0, level),
        };
      });
      setPageChanged(false);
    },
    [setNavigation]
  );

  const closeSubEntry = useCallback(
    async (id: string, level: number) => {
      setNavigation((prevState: NavigationEntry) => {
        return {
          ...prevState,
          children: setOpenState(id, prevState.children, false, 0, level),
        };
      });
      setPageChanged(false);
    },
    [setNavigation]
  );

  const selectEntry = useCallback(
    async (id: string) => {
      setNavigation((prevState: NavigationEntry) => {
        return {
          ...prevState,
          children: selectSingleEntry(id, prevState.children),
        };
      });
      setPageChanged(false);
    },
    [setNavigation]
  );

  const [editMode, setEditMode] = useState(false);
  //const [myNews, setMyNews] = useState<NavigationEntry[]>([]);

  const addToMyNews = (item: PersonalNavigationEntry) => {
    if (personalResorts.length <= 2 && !isItemIncluded(item))
      setPersonalResorts([...personalResorts, item]);
  };

  const isItemIncluded = (item: PersonalNavigationEntry) => {
    return personalResorts.find((m) => m.id == item.id) != undefined;
  };

  const removeFromMyNews = (item: PersonalNavigationEntry) => {
    setPersonalResorts(personalResorts.filter((e) => e !== item));
  };

  function renderSubMenu(
    entries: NavigationEntry[],
    level: number,
    pageId: string,
    isApp: boolean,
    pageChanged: boolean
  ) {
    return entries.map((e) => {
      return (
        <NavigationItem
          key={e.id}
          navigation={e}
          level={level}
          renderSubMenu={renderSubMenu}
          loadSubEntry={loadSubEntry}
          openSubEntry={openSubEntry}
          closeSubEntry={closeSubEntry}
          selectEntry={selectEntry}
          toggleNavbar={toggleOverlay}
          pageId={pageId}
          isApp={isApp}
          pageChanged={pageChanged}
          editMode={editMode}
          myNews={personalResorts}
          setMyNews={setPersonalResorts}
          removeFromMyNews={removeFromMyNews}
          addToMyNews={addToMyNews}
        />
      );
    });
  }

  const [headerHeight] = useHeaderHeight();
  const [documentHeight, setDocumentHeight] = useState<number | null>(null);
  const getHeights = () => {
    setDocumentHeight(window.innerHeight);
  };
  useEffect(() => {
    getHeights();
    window.addEventListener("resize", getHeights);
  }, []);

  return (
    <>
      {navigation && (
        <div
          role="navigation"
          ref={navRef}
          className={`nav ${collapsed ? "collapsed" : ""}`}
          style={{
            top: headerHeight + "px",
            height: (documentHeight ?? 0) - headerHeight + "px",
          }}
        >
          <div className="nav-container">
            <React.Fragment>
              <NavigationSearch toggleOverlay={toggleOverlay} />
              <div className="fav-header-item">
                <MyFavs
                  setEditMode={setEditMode}
                  editMode={editMode}
                  selectEntry={selectEntry}
                />
              </div>
            </React.Fragment>

            {navigation?.trendingTopics?.length > 0 && (
              <div className="header-item first">
                <div className="nav-header">
                  <span className="nav-header__icon">
                    <ThumbsUp />
                  </span>
                  <span>Empfehlungen</span>
                </div>
                {navigation.trendingTopics.map((e) => {
                  const linkObject = getLinkObjectFromTeaser(e);
                  const hasLink = linkObject && (linkObject.internalLink || linkObject.externalLink);
                  return (
                    <div key={`trendingTopics-${e.id}`} className="nav-item">
                      <div className="nav-item-text-wrapper">
                        {hasLink && linkObject.externalLink && (
                          <Link
                            href={linkObject.externalLink}
                            target="_blank"
                            className="nav-item-text"
                          >
                            {e.title}
                          </Link>
                        )}
                        {props.isApp == false && hasLink && linkObject.internalLink && (
                            <Link
                              href={linkObject.internalLink}
                              className="nav-item-text"
                              onClick={() => {
                                toggleOverlay();
                              }}
                            >
                              {e.title}
                            </Link>
                          )}
                        {props.isApp == true &&
                          hasLink && linkObject.internalLink && (
                            <a
                              href={linkObject.internalLink}
                              className="nav-item-text"
                              onClick={() => {
                                toggleOverlay();
                              }}
                            >
                              {e.title}
                            </a>
                          )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="header-item">
              <div className="nav-header">
                <span className="nav-header__icon">
                  <MapPin />
                </span>
                <span>
                  {process.env.NEXT_PUBLIC_APP_THEME == AppTheme.nn
                    ? "Nachrichten & Themen"
                    : "Regionen & Kategorien"}
                </span>
              </div>
              {navigation.children &&
                renderSubMenu(
                  navigation.children,
                  0,
                  pageId ?? '',
                  props.isApp == true,
                  pageChanged
                )}

              {props.isApp !== true && (
                <div className="">
                  <div className="nav-item">
                    <div className="nav-item-text-wrapper">
                      <Link
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          showCmp();
                        }}
                        className="nav-item-text"
                      >
                        Cookies & Tracking
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              {props.isApp === true && (
                <>
                  <div className="nav-item">
                    <div className="nav-item-text-wrapper">
                      <CmpLink />
                    </div>
                  </div>
                  {/* <div className="nav-item">
                    <div className="nav-item-text-wrapper">
                    <a href={`${process.env.NEXT_PUBLIC_VNP_BASE_URL}/barrierefreiheit`} target="_blank" className="nav-item-text">Barrierefreiheit</a>
                    </div>
                  </div> */}
                </>
              )}
            </div>
            {subscriptions?.length > 0 && (appInfo.pushesEnabled || pushSubscriptionId) && (
              <div className="header-item">
                <div className="nav-header">Mitteilungen</div>
                {subscriptions.map((entry) => {
                  return (
                    <li key={entry.id} className="nav-item">
                      <div className="nav-item-text-wrapper">
                        <Toggle
                          onclick={() => {
                            togglePushes(entry.id);
                          }}
                          label={entry.text}
                          checked={entry.isSubscribed}
                          textClassName="nav-link app"
                          isProcessing={isProcessing ?? false}
                        />
                      </div>
                    </li>
                  );
                })}
                {/* {(appInfo.pushSubscriptionId || pushSubscriptionId) && (
                  <div className="nav-item ">
                    <div className="nav-item-text-wrapper">
                      <span className="pr-2 icon-big"></span>
                      <div className="nav-item-text">
                        {appInfo.pushSubscriptionId ?? pushSubscriptionId}
                      </div>
                    </div>
                  </div>
                )} */}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
